import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { Tag, Plus, Copy, Link2, Loader2, Trash2, Shuffle, ChevronDown, ChevronUp, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const safeCopy = async (text, msg) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(msg);
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
      toast.success(msg);
    } catch { toast.info(text); }
  }
};

const STATE_BADGE = {
  live: 'bg-emerald-500/15 text-emerald-400',
  scheduled: 'bg-sky-500/15 text-sky-400',
  ended: 'bg-muted text-muted-foreground',
  exhausted: 'bg-amber-500/15 text-amber-400',
  inactive: 'bg-rose-500/15 text-rose-400',
};

export const DiscountCodesTab = ({ eventId, token, event }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [discounts, setDiscounts] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', type: 'percent', value: '', applies_to_ticket_type_ids: [], max_uses: '', starts_at: '', ends_at: '' });
  const [openRedemptions, setOpenRedemptions] = useState(null);
  const [redemptions, setRedemptions] = useState({});

  const loadRedemptions = async (d) => {
    if (openRedemptions === d.discount_id) { setOpenRedemptions(null); return; }
    setOpenRedemptions(d.discount_id);
    try {
      const res = await axios.get(`${API}/events/${eventId}/discounts/${d.discount_id}/redemptions`, { headers });
      setRedemptions(r => ({ ...r, [d.discount_id]: res.data }));
    } catch { toast.error('Could not load redemptions'); }
  };

  const load = async () => {
    try {
      const [d, t] = await Promise.all([
        axios.get(`${API}/events/${eventId}/discounts`, { headers }),
        axios.get(`${API}/events/${eventId}/ticket-types`),
      ]);
      setDiscounts(d.data.discounts || []);
      setTicketTypes((t.data.ticket_types || t.data || []).filter(tt => !tt.is_addon));
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [eventId]);

  const eventUrl = `${window.location.origin}/events/${event?.city || 'denver'}/${event?.slug || ''}`;
  const autoLink = (code) => `${eventUrl}?discount=${encodeURIComponent(code)}`;

  const genCode = () => {
    const words = ['SAVE', 'VIP', 'FRIEND', 'EARLY', 'CREW', 'GUEST'];
    const w = words[Math.floor(Math.random() * words.length)];
    setForm(f => ({ ...f, code: `${w}${Math.floor(10 + Math.random() * 90)}` }));
  };

  const create = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/events/${eventId}/discounts`, {
        ...form,
        value: parseFloat(form.value || 0),
        max_uses: parseInt(form.max_uses || 0, 10),
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      }, { headers });
      toast.success(`Code ${form.code.toUpperCase()} created`);
      setForm({ code: '', type: 'percent', value: '', applies_to_ticket_type_ids: [], max_uses: '', starts_at: '', ends_at: '' });
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create code'); }
    finally { setCreating(false); }
  };

  const toggleActive = async (d) => {
    try {
      await axios.put(`${API}/events/${eventId}/discounts/${d.discount_id}`, { is_active: !d.is_active }, { headers });
      load();
    } catch { toast.error('Update failed'); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete code ${d.code}?`)) return;
    try {
      await axios.delete(`${API}/events/${eventId}/discounts/${d.discount_id}`, { headers });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const toggleTT = (id) => setForm(f => ({
    ...f,
    applies_to_ticket_type_ids: f.applies_to_ticket_type_ids.includes(id)
      ? f.applies_to_ticket_type_ids.filter(x => x !== id)
      : [...f.applies_to_ticket_type_ids, id],
  }));

  if (loading) return <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <div className="space-y-4" data-testid="discounts-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-bold text-sm flex items-center gap-2"><Tag className="h-4 w-4 text-emerald-400" /> Discount Codes</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Percent or $ off per ticket, limited to chosen ticket types — share the code or an auto-apply link.</p>
        </div>
        <Button size="sm" onClick={() => setShowForm(s => !s)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-discount-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Code
        </Button>
      </div>

      {showForm && (
        <Card className="border-emerald-500/30">
          <CardContent className="p-4 space-y-3" data-testid="discount-form">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Code</Label>
                <div className="flex gap-1">
                  <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '') }))} placeholder="SAVE20" data-testid="discount-code-input" />
                  <Button type="button" variant="outline" size="icon" className="flex-shrink-0" onClick={genCode} title="Generate"><Shuffle className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Discount</Label>
                <div className="flex gap-1">
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="h-10 rounded-md border border-input bg-background px-2 text-sm" data-testid="discount-type-select">
                    <option value="percent">% off</option>
                    <option value="amount">$ off / ticket</option>
                  </select>
                  <Input type="number" min="0" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} placeholder={form.type === 'percent' ? '20' : '10.00'} data-testid="discount-value-input" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Usage limit <span className="text-muted-foreground">(tickets, 0 = unlimited)</span></Label>
                <Input type="number" min="0" value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} placeholder="0" data-testid="discount-limit-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Starts (optional)</Label>
                <Input type="datetime-local" value={form.starts_at} onChange={e => setForm(f => ({ ...f, starts_at: e.target.value }))} data-testid="discount-starts-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Ends (optional)</Label>
                <Input type="datetime-local" value={form.ends_at} onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))} data-testid="discount-ends-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Applies to <span className="text-muted-foreground">(none selected = all tickets)</span></Label>
              <div className="flex flex-wrap gap-1.5">
                {ticketTypes.map(tt => (
                  <button key={tt.ticket_type_id} type="button" onClick={() => toggleTT(tt.ticket_type_id)}
                    className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${form.applies_to_ticket_type_ids.includes(tt.ticket_type_id) ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-border text-muted-foreground hover:border-emerald-500/50'}`}
                    data-testid={`discount-tt-${tt.ticket_type_id}`}>
                    {tt.name} {tt.price > 0 ? `($${tt.price})` : '(Free)'}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={create} disabled={creating || !form.code || !form.value} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-discount-btn">
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Tag className="h-4 w-4 mr-2" />} Create Code
            </Button>
          </CardContent>
        </Card>
      )}

      {discounts.length === 0 && !showForm && (
        <p className="text-xs text-muted-foreground text-center py-6">No discount codes yet — create your first one.</p>
      )}

      <div className="space-y-2">
        {discounts.map(d => (
          <Card key={d.discount_id} data-testid={`discount-row-${d.code}`}>
            <CardContent className="p-3.5">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono font-bold text-sm">{d.code}</span>
                <Badge className={`text-[9px] ${STATE_BADGE[d.state] || ''}`}>{d.state}</Badge>
                <span className="text-xs text-emerald-400 font-semibold">{d.type === 'percent' ? `${d.value}% off` : `$${Number(d.value).toFixed(2)} off/ticket`}</span>
                <span className="text-[11px] text-muted-foreground">
                  {(d.applies_to_ticket_type_ids || []).length
                    ? `${d.applies_to_ticket_type_ids.length} ticket type${d.applies_to_ticket_type_ids.length === 1 ? '' : 's'}`
                    : 'All tickets'}
                </span>
                <span className="text-[11px] text-muted-foreground" data-testid={`discount-usage-${d.code}`}>
                  Used {d.times_used || 0}{d.max_uses ? `/${d.max_uses}` : ''}
                  {d.max_uses ? ` · ${Math.max(0, d.max_uses - (d.times_used || 0))} left` : ' · unlimited'}
                  {' '}· {d.orders_count || 0} order{(d.orders_count || 0) === 1 ? '' : 's'} · ${((d.total_discounted_cents || 0) / 100).toFixed(2)} given
                </span>
                <div className="ml-auto flex items-center gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => loadRedemptions(d)} data-testid={`redemptions-btn-${d.code}`}>
                    <Users className="h-3 w-3 mr-1" /> Who used it {openRedemptions === d.discount_id ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => safeCopy(d.code, 'Code copied')} data-testid={`copy-code-${d.code}`}>
                    <Copy className="h-3 w-3 mr-1" /> Code
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => safeCopy(autoLink(d.code), 'Auto-apply link copied — the discount applies the moment they open it')} data-testid={`copy-link-${d.code}`}>
                    <Link2 className="h-3 w-3 mr-1" /> Link
                  </Button>
                  <Switch checked={!!d.is_active} onCheckedChange={() => toggleActive(d)} data-testid={`toggle-${d.code}`} />
                  {(d.times_used || 0) === 0 && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-rose-400" onClick={() => remove(d)} data-testid={`delete-${d.code}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              {openRedemptions === d.discount_id && (
                <div className="mt-3 pt-3 border-t border-border/50" data-testid={`redemptions-list-${d.code}`}>
                  {!redemptions[d.discount_id] ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mx-auto" />
                  ) : (redemptions[d.discount_id].redemptions || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">No one has used this code yet.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-56 overflow-y-auto">
                      {(redemptions[d.discount_id].redemptions || []).map(r => (
                        <div key={r.order_id} className="flex items-center justify-between text-xs gap-2" data-testid={`redemption-${r.order_id}`}>
                          <div className="min-w-0">
                            <span className="font-medium">{r.buyer_name}</span>
                            <span className="text-muted-foreground ml-1.5">{r.buyer_email}</span>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 text-muted-foreground">
                            <span>{r.discount_tickets} tkt{r.discount_tickets === 1 ? '' : 's'}</span>
                            <span className="text-emerald-400 font-medium">-${(r.discount_total || 0).toFixed(2)}</span>
                            <span>{r.created_at ? new Date(r.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default DiscountCodesTab;
